import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { DataService } from '@data/data.service';
import { ToolbarService } from '../../toolbar/toolbar.service';
import { isJsObject, parseDec, trimString } from '@util/utils';
import { NotificationService } from '@notification/notification.service';
import { InputComponent } from '@input/input/input.component';
import { ErrorElemContainer } from '@util/error-elem-container';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { BackendPaths } from '@common/backend-paths';
import { emailIsValid, validateLength } from '@util/input-utils';
import { InputFieldNames, InputFieldSizeType } from '@common/input-definitions';
import { NavigationService } from '@services/navigation.service';
import { StateService } from '@services/state.service';

@Component({
  selector: 'app-team',
  templateUrl: './team.component.html'
})
export class TeamComponent implements OnInit {

  title = 'Team';

  id: number = 0;

  data: any = {};

  emailLength!: InputFieldSizeType;
  firstNameLength!: InputFieldSizeType;
  lastNameLength!: InputFieldSizeType;
  phoneLength!: InputFieldSizeType;
  userNameLength!: InputFieldSizeType;

  private _nr!: InputComponent;

  @ViewChild('nr', {
    read: InputComponent
  }) set nr(nr: InputComponent) {
    this._nr = nr;
  }

  private _email!: InputComponent;
  @ViewChild('email', {
    read: InputComponent
  }) set email(email: InputComponent) {
    this._email = email;
  }

  private _fname!: InputComponent;
  @ViewChild('fname', {
    read: InputComponent
  }) set fname(fname: InputComponent) {
    this._fname = fname;
  }

  private _lname!: InputComponent;
  @ViewChild('lname', {
    read: InputComponent
  }) set lname(lname: InputComponent) {
    this._lname = lname;
  }

  private _username!: InputComponent;
  @ViewChild('username', {
    read: InputComponent
  }) set username(username: InputComponent) {
    this._username = username;
  }

  constructor(
    private currentRoute: ActivatedRoute,
    private dataService: DataService,
    private navService: NavigationService,
    public notificationService: NotificationService,
    private stateService: StateService,
    private toolbarService: ToolbarService) {
  }

  initSizes() {
    this.emailLength = this.stateService.getFieldSize(
      InputFieldNames.TEAM_EMAIL
    );
    this.firstNameLength = this.stateService.getFieldSize(
      InputFieldNames.TEAM_FIRSTNAME
    );
    this.lastNameLength = this.stateService.getFieldSize(
      InputFieldNames.TEAM_LASTNAME
    );
    this.phoneLength = this.stateService.getFieldSize(
      InputFieldNames.TEAM_MOBILE
    );
    this.userNameLength = this.stateService.getFieldSize(
      InputFieldNames.TEAM_USERNAME
    );
  }

  ngOnInit(): void {
    this.initSizes();

    this.toolbarService.setTitle(this.title);

    this.currentRoute.params.subscribe((param: Params) => {
      if (param.id) {
        this.id = parseDec(param.id);
      }
      this.getData();
    });
  }

  backendUrl() {
    return BackendPaths.TEAMS_FULL + this.id;
  }

  getData() {
    if (this.id) {
      const url = this.backendUrl();

      this.dataService.get(url).subscribe((data: any) => {
        const result = data?.result;
        const content = result?.data;

        if (isJsObject(content)) {

          content.inactive = false;

          if ('active' in content) {
            content.inactive = content.active ? false : true;
          }

          this.data = content;
        } else {
          this.notificationService.showErrors(
            [
              'Fehler Serveranfrage',
              'Bitte Entwickler kontaktieren'
            ]
          );
        }
      });
    }
  }

  onNameChange() {
    const parts = [];

    if (this.data.first_name) {
      parts.push(this.data.first_name);
    }

    if (this.data.last_name) {
      parts.push(this.data.last_name);
    }
    const name = parts.join(' ');

    let title = this.title;

    if (name) {
      title += ': ' + name;
    }

    this.toolbarService.setTitle(title);
  }

  clickCancel() {
    this.navService.goBack();
  }

  clickSave() {

    this.data.first_name = trimString(this.data.first_name);
    this.data.last_name = trimString(this.data.last_name);
    this.data.mobile = trimString(this.data.mobile);
    this.data.team_number = trimString(this.data.team_number);
    this.data.email = trimString(this.data.email);

    this.data.active = this.data.inactive ? 0 : 1;

    const errorMessages: string[] = [];
    const errorElems = new ErrorElemContainer();

    if (this.data.team_number) {
      const nr = parseDec(this.data.team_number);
      if (isNaN(nr) || '' + nr !== '' + this.data.team_number) {
        errorElems.add(this._nr);
        errorMessages.push('Nr. ist keine gültige Zahl');
      }
    } else {
      errorElems.add(this._nr);
      errorMessages.push('Nr. fehlt');
    }

    if (!this.data.first_name) {
      errorElems.add(this._fname);
      errorMessages.push('Vorname fehlt');
    } else if (!validateLength(this.data.first_name, this.firstNameLength)) {
      errorElems.add(this._fname);
      errorMessages.push('Vorname ist zu lang');
    }

    if (!this.data.last_name) {
      errorElems.add(this._lname);
      errorMessages.push('Nachname fehlt');
    } else if (!validateLength(this.data.last_name, this.lastNameLength)) {
      errorElems.add(this._lname);
      errorMessages.push('Nachname ist zu lang');
    }

    if (!this.data.username) {
      errorElems.add(this._username);
      errorMessages.push('Benutzername fehlt');
    } else if (!validateLength(this.data.username, this.lastNameLength)) {
      errorElems.add(this._username);
      errorMessages.push('Benutzername ist zu lang');
    }

    if (this.data.email) {
      if (!emailIsValid(this.data.email)) {
        errorElems.add(this._email);
        errorMessages.push('E-Mail ungültig');
      } else if (!validateLength(this.data.email, this.emailLength)) {
        errorElems.add(this._email);
        errorMessages.push('E-Mail ist zu lang');
      }
    } else {
      errorElems.add(this._email);
      errorMessages.push('E-Mail fehlt');
    }

    errorElems.highlightErrors();

    if (errorMessages.length > 0) {
      this.notificationService.showErrors(errorMessages);
    } else {
      const url = BackendPaths.TEAMS;
      this.data.id = this.id;

      this.dataService.post(url, this.data).subscribe((response: any) => {
        const result = response?.result;

        if (result?.success) {
          let msg = '';
          if (Array.isArray(result.errors)) {
            msg = ' mit Fehlern';
          }
          this.notificationService.showSuccess(
            'Teamdaten gespeichert' + msg,
            [],
            result.errors
          );

          this.navService.goBack();
          return;
        }

        let errors: any[] = [
          'Unbekannter Fehler beim Speichern'
        ];

        if (Array.isArray(result?.errors)) {
          errors = result.errors;
        }

        this.notificationService.showErrors(errors);
      });
    }
  }

  newPassword() {
    const url = BackendPaths.TEAMS_PASSWORD;

    const data = {
      id: this.id
    };

    this.dataService.post(url, data).subscribe((response: any) => {
      const result = response?.result;

      if (result?.success) {
        this.notificationService.showSuccess(
          result.msg,
          result.messages,
          result.errors
        );

        this.navService.goBack();
        return;
      }

      let errors: any[] = [
        'Unbekannter Fehler beim Erzeugen des Passworts'
      ];

      if (Array.isArray(result?.errors)) {
        errors = result.errors;
      }

      this.notificationService.showErrors(errors);
    });
  }

  onActiveChange($event: MatCheckboxChange) {
    if ($event) {
      this.data.inactive = $event.checked;
    }
  }
}
